import { FC, useState, useEffect, useRef, ChangeEvent, FormEvent, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import colors from '../../styles/colors';
import CommonWrapper from '../../components/ui_components/AdminWrapper';
import {
  apiCreateWrestlerV2,
  apiEditWrestlerV2,
  apiListWrestler,
} from '../../api/wrestlerApi';
import type { Wrestler } from '../../types';

// ── List Styled Components ─────────────────────────────────────────────────────

const Container = styled.div`
  width: 80%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
`;

const PageHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  font-size: 14px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background-color: ${colors.bgColorBlack2};
  color: white;
  min-width: 220px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
  }
`;

const CreateBtn = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${colors.bgColorBlack2};
  color: white;
  border: 1px solid white;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  text-transform: uppercase;
  transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
    background-color: ${colors.bgColorBlack};
  }
`;

const CountLabel = styled.p`
  width: 100%;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  margin: 0 0 8px;
`;

const WrestlerListGrid = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WrestlerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  background-color: ${colors.bgColorBlack2};
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out, transform 0.2s ease-in-out;

  &:hover {
    border-color: gold;
    transform: scale(1.01);
  }
`;

const WrestlerAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.05);
`;

const WrestlerName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: gold;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const Badge = styled.span<{ $bg?: string; $dark?: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  background-color: ${({ $bg }) => $bg ?? 'rgba(255,255,255,0.12)'};
  color: ${({ $dark }) => ($dark ? '#1a1a19' : 'white')};
  white-space: nowrap;
`;

const EditBtn = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  background-color: ${colors.bgColorBlack};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;

  &:hover {
    border-color: white;
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

// ── Modal Styled Components ────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalBox = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 2px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  width: 100%;
  max-width: 780px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px 28px 28px;
  position: relative;

  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: white;
  }
`;

// ── Form Styled Components ─────────────────────────────────────────────────────

const FormTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 4px;
  color: gold;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin: 0 0 22px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormLabel = styled.label`
  display: inline-block;
  margin-bottom: 6px;
  font-weight: 500;
  color: gold;
  font-size: 13px;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #000;
  background-color: #fff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
  }

  &[type='file'] {
    padding: 7px 8px;
    cursor: pointer;
    color: #333;
    background-color: #f8f9fa;

    &::file-selector-button {
      padding: 5px 12px;
      margin-right: 12px;
      border: 1px solid #d3d3d3;
      border-radius: 4px;
      background-color: #f8f9fa;
      color: #000;
      cursor: pointer;
      font-size: 13px;

      &:hover {
        background-color: #e9ecef;
      }
    }
  }

  &[type='number'] {
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &[type='checkbox'] {
    width: auto;
    cursor: pointer;
  }
`;

const FormTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #000;
  background-color: #fff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  box-sizing: border-box;
  min-height: 90px;
  resize: vertical;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
  }
`;

const CurrentAvatarPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);

  img {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-size: 14px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const BtnBase = styled.button`
  padding: 9px 22px;
  font-size: 14px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.15s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled(BtnBase)`
  background-color: ${colors.bgColorBordeaux};
  color: white;
  border: 1px solid ${colors.bgColorBordeaux};

  &:hover:not(:disabled) {
    background-color: ${colors.bgColorRed};
    border-color: ${colors.bgColorRed};
  }
`;

const BtnSecondary = styled(BtnBase)`
  background-color: ${colors.bgColorBlack2};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover:not(:disabled) {
    background-color: ${colors.bgColorBlack};
    border-color: rgba(255, 255, 255, 0.6);
  }
`;

// ── Shared ─────────────────────────────────────────────────────────────────────

const SuccessMessage = styled.div`
  padding: 10px 14px;
  background-color: rgba(40, 167, 69, 0.15);
  border: 1px solid rgba(40, 167, 69, 0.4);
  color: #a3d9a5;
  border-radius: 4px;
  margin-bottom: 16px;
  text-align: center;
  font-size: 14px;
`;

const ErrorText = styled.p`
  margin: 5px 0 0;
  color: #f07070;
  font-size: 13px;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 15px;
  padding: 40px 0;
  margin: 0;
`;

// ── Types ──────────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  realFaceName: string;
  placeBirth: string;
  catchphrase: string;
  weight: string;
  height: string;
  themeMusic: string;
  bio: string;
  finisher: string;
  isFemale: boolean;
  isActive: boolean;
  isManager: boolean;
  forumUrl: string;
}

interface FormErrors {
  name?: string;
  general?: string;
}

const emptyFormState: FormState = {
  name: '',
  realFaceName: '',
  placeBirth: '',
  catchphrase: '',
  weight: '',
  height: '',
  themeMusic: '',
  bio: '',
  finisher: '',
  isFemale: false,
  isActive: true,
  isManager: false,
  forumUrl: '',
};

const wrestlerToForm = (w: Wrestler): FormState => ({
  name: w.name ?? '',
  realFaceName: w.real_face_name ?? '',
  placeBirth: w.place_birth ?? '',
  catchphrase: w.catchphrase ?? '',
  weight: w.weight != null ? String(w.weight) : '',
  height: w.height != null ? String(w.height) : '',
  themeMusic: w.theme_music ?? '',
  bio: w.bio ?? '',
  finisher: w.finisher ?? '',
  isFemale: !!w.is_female,
  isActive: !!w.is_active,
  isManager: !!w.is_manager,
  forumUrl: w.forum_url ?? '',
});

// ── Component ──────────────────────────────────────────────────────────────────

const HandleWrestler: FC = () => {
  // ── List state ──────────────────────────────────────────────────────────────
  const [wrestlers, setWrestlers] = useState<Wrestler[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Modal state ──────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editWrestlerId, setEditWrestlerId] = useState<number | null>(null);

  // ── Form state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState<FormState>(emptyFormState);
  const [fileAvatar, setFileAvatar] = useState<File | null>(null);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
    };
  }, []);

  // ── Load list ────────────────────────────────────────────────────────────────
  const loadList = useCallback(() => {
    setIsListLoading(true);
    setListError(null);
    apiListWrestler()
      .then((data) => {
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
        setWrestlers(sorted);
      })
      .catch((err) => {
        console.error(err);
        setListError('Errore nel caricamento dei wrestler.');
      })
      .finally(() => { setIsListLoading(false); });
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ── Filtered wrestlers ───────────────────────────────────────────────────────
  const filteredWrestlers = useMemo(() => {
    if (!searchQuery.trim()) return wrestlers;
    const q = searchQuery.toLowerCase();
    return wrestlers.filter((w) => w.name.toLowerCase().includes(q));
  }, [wrestlers, searchQuery]);

  // ── Modal helpers ────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setModalMode('create');
    setEditWrestlerId(null);
    setForm(emptyFormState);
    setFileAvatar(null);
    setCurrentAvatarUrl(null);
    setErrors({});
    setSuccess(false);
    setModalOpen(true);
  };

  const openEditModal = (wrestler: Wrestler) => {
    setModalMode('edit');
    setEditWrestlerId(wrestler.id);
    setForm(wrestlerToForm(wrestler));
    setFileAvatar(null);
    setCurrentAvatarUrl(wrestler.avatar_path || null);
    setErrors({});
    setSuccess(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalOpen(false);
  };

  // ── Form helpers ─────────────────────────────────────────────────────────────
  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    const nextErrors: FormErrors = {};
    if (!form.name || form.name.trim().length === 0) {
      nextErrors.name = 'Inserisci un nome';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const fd = new FormData();
    fd.append('name', form.name.trim());
    if (form.realFaceName.trim()) fd.append('real_face_name', form.realFaceName.trim());
    if (form.placeBirth.trim()) fd.append('place_birth', form.placeBirth.trim());
    if (form.catchphrase.trim()) fd.append('catchphrase', form.catchphrase.trim());
    if (form.weight.trim()) {
      const w = parseInt(form.weight, 10);
      if (!isNaN(w) && w >= 0) fd.append('weight', String(w));
    }
    if (form.height.trim()) {
      const h = parseInt(form.height, 10);
      if (!isNaN(h) && h >= 0) fd.append('height', String(h));
    }
    if (fileAvatar) fd.append('file_avatar_wrestler', fileAvatar);
    if (form.themeMusic.trim()) fd.append('theme_music', form.themeMusic.trim());
    if (form.bio.trim()) fd.append('bio', form.bio.trim());
    if (form.finisher.trim()) fd.append('finisher', form.finisher.trim());
    fd.append('is_female', form.isFemale ? '1' : '0');
    fd.append('is_active', form.isActive ? '1' : '0');
    fd.append('is_manager', form.isManager ? '1' : '0');
    if (form.forumUrl.trim()) fd.append('forum_url', form.forumUrl.trim());

    setIsSaving(true);
    try {
      let response;
      if (modalMode === 'edit' && editWrestlerId) {
        response = await apiEditWrestlerV2(fd, editWrestlerId);
      } else {
        response = await apiCreateWrestlerV2(fd);
      }

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        const firstVal = response.errors[firstKey as keyof typeof response.errors];
        const msg = Array.isArray(firstVal)
          ? firstVal[0]
          : (firstVal ?? 'Errore di validazione');
        setErrors({ general: String(msg) });
        return;
      }

      setSuccess(true);
      submitTimeoutRef.current = setTimeout(() => {
        setModalOpen(false);
        loadList();
      }, 1200);
    } catch (err) {
      console.error('Errore durante il salvataggio:', err);
      const msg =
        err instanceof Error ? err.message : 'Errore durante il salvataggio. Riprova.';
      setErrors({ general: msg });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <CommonWrapper className="handle-wrestler-wrapper" style={{ background: colors.bgGradientSfondoColor }}>
      <Container>
        {/* Header */}
        <PageHeader>
          <PageTitle>Gestisci Wrestler</PageTitle>
          <PageControls>
            <SearchInput
              type="text"
              placeholder="Cerca per nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <CreateBtn onClick={openCreateModal}>+ Crea Nuovo</CreateBtn>
          </PageControls>
        </PageHeader>

        {/* List */}
        {isListLoading && <StatusText>Caricamento wrestler...</StatusText>}
        {listError && (
          <ErrorText style={{ textAlign: 'center', padding: '20px 0' }}>{listError}</ErrorText>
        )}

        {!isListLoading && !listError && (
          <>
            <CountLabel>{filteredWrestlers.length} wrestler</CountLabel>
            <WrestlerListGrid>
              {filteredWrestlers.map((w) => (
                <WrestlerCard key={w.id}>
                  <WrestlerAvatar
                    src={process.env.REACT_APP_STORAGE_URL + w.avatar_path}
                    alt={w.name}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                  <WrestlerName>{w.name}</WrestlerName>
                  <BadgeRow>
                    {!!w.is_female && <Badge $bg="gold" $dark>F</Badge>}
                    {!!w.is_manager && <Badge $bg={colors.bgColorBordeaux}>Mgr</Badge>}
                  </BadgeRow>
                  <EditBtn onClick={() => openEditModal(w)}>Modifica</EditBtn>
                </WrestlerCard>
              ))}
            </WrestlerListGrid>
          </>
        )}
      </Container>

      {/* Modal */}
      {modalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalCloseBtn type="button" onClick={closeModal} aria-label="Chiudi">
              ×
            </ModalCloseBtn>

            <FormTitle>
              {modalMode === 'create' ? 'Aggiungi Wrestler' : 'Modifica Wrestler'}
            </FormTitle>
            {modalMode === 'edit' && form.name && (
              <FormSubtitle>{form.name}</FormSubtitle>
            )}

            {success && (
              <SuccessMessage>
                {modalMode === 'create'
                  ? 'Wrestler creato con successo!'
                  : 'Wrestler aggiornato con successo!'}
              </SuccessMessage>
            )}

            {errors.general && (
              <ErrorText style={{ marginBottom: '14px', textAlign: 'center' }}>
                {errors.general}
              </ErrorText>
            )}

            <form onSubmit={handleSubmit}>
              {/* Nome */}
              <FormGroup>
                <FormLabel htmlFor="hw-name">Nome Wrestler *</FormLabel>
                <FormInput
                  id="hw-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Inserisci nome..."
                />
                {errors.name && <ErrorText>{errors.name}</ErrorText>}
              </FormGroup>

              {/* Prestavolto + Luogo nascita */}
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="hw-real_face_name">Prestavolto di</FormLabel>
                  <FormInput
                    id="hw-real_face_name"
                    type="text"
                    value={form.realFaceName}
                    onChange={(e) => updateField('realFaceName', e.target.value)}
                    placeholder="Inserisci prestavolto..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="hw-place_birth">Luogo di nascita</FormLabel>
                  <FormInput
                    id="hw-place_birth"
                    type="text"
                    value={form.placeBirth}
                    onChange={(e) => updateField('placeBirth', e.target.value)}
                    placeholder="Inserisci luogo di nascita..."
                  />
                </FormGroup>
              </FormRow>

              {/* Catchphrase */}
              <FormGroup>
                <FormLabel htmlFor="hw-catchphrase">Catchphrase</FormLabel>
                <FormInput
                  id="hw-catchphrase"
                  type="text"
                  value={form.catchphrase}
                  onChange={(e) => updateField('catchphrase', e.target.value)}
                  placeholder="Inserisci catchphrase..."
                />
              </FormGroup>

              {/* Peso + Altezza */}
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="hw-weight">Peso (kg)</FormLabel>
                  <FormInput
                    id="hw-weight"
                    type="number"
                    min="0"
                    value={form.weight}
                    onChange={(e) => updateField('weight', e.target.value)}
                    placeholder="Es: 100"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="hw-height">Altezza (cm)</FormLabel>
                  <FormInput
                    id="hw-height"
                    type="number"
                    min="0"
                    value={form.height}
                    onChange={(e) => updateField('height', e.target.value)}
                    placeholder="Es: 180"
                  />
                </FormGroup>
              </FormRow>

              {/* Theme Music */}
              <FormGroup>
                <FormLabel htmlFor="hw-theme_music">Nome Theme Music</FormLabel>
                <FormInput
                  id="hw-theme_music"
                  type="text"
                  value={form.themeMusic}
                  onChange={(e) => updateField('themeMusic', e.target.value)}
                  placeholder="Inserisci nome theme music..."
                />
              </FormGroup>

              {/* Finisher */}
              <FormGroup>
                <FormLabel htmlFor="hw-finisher">Finisher</FormLabel>
                <FormInput
                  id="hw-finisher"
                  type="text"
                  value={form.finisher}
                  onChange={(e) => updateField('finisher', e.target.value)}
                  placeholder="Inserisci finisher..."
                />
              </FormGroup>

              {/* Bio */}
              <FormGroup>
                <FormLabel htmlFor="hw-bio">Biografia</FormLabel>
                <FormTextarea
                  id="hw-bio"
                  value={form.bio}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    updateField('bio', e.target.value)
                  }
                  placeholder="Inserisci biografia..."
                />
              </FormGroup>

              {/* Forum URL */}
              <FormGroup>
                <FormLabel htmlFor="hw-forum_url">Forum URL</FormLabel>
                <FormInput
                  id="hw-forum_url"
                  type="url"
                  value={form.forumUrl}
                  onChange={(e) => updateField('forumUrl', e.target.value)}
                  placeholder="https://..."
                />
              </FormGroup>

              {/* Avatar */}
              <FormGroup>
                <FormLabel htmlFor="hw-file_avatar">Avatar Wrestler</FormLabel>
                {modalMode === 'edit' && currentAvatarUrl && (
                  <CurrentAvatarPreview>
                    <img
                      src={process.env.REACT_APP_STORAGE_URL + currentAvatarUrl}
                      alt="avatar attuale"
                    />
                    <span>Avatar attuale — lascia vuoto per mantenerlo</span>
                  </CurrentAvatarPreview>
                )}
                <FormInput
                  id="hw-file_avatar"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/webp"
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFileAvatar(e.target.files?.[0] ?? null)
                  }
                />
              </FormGroup>

              {/* Checkboxes */}
              <FormRow>
                <FormGroup>
                  <CheckboxLabel>
                    <FormInput
                      id="hw-is_female"
                      type="checkbox"
                      checked={form.isFemale}
                      onChange={(e) => updateField('isFemale', e.target.checked)}
                    />
                    <span>È donna</span>
                  </CheckboxLabel>
                </FormGroup>
                <FormGroup>
                  <CheckboxLabel>
                    <FormInput
                      id="hw-is_active"
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => updateField('isActive', e.target.checked)}
                    />
                    <span>È attivo</span>
                  </CheckboxLabel>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <CheckboxLabel>
                  <FormInput
                    id="hw-is_manager"
                    type="checkbox"
                    checked={form.isManager}
                    onChange={(e) => updateField('isManager', e.target.checked)}
                  />
                  <span>È manager</span>
                </CheckboxLabel>
              </FormGroup>

              <ButtonRow>
                <BtnSecondary type="button" onClick={closeModal} disabled={isSaving}>
                  Annulla
                </BtnSecondary>
                <BtnPrimary type="submit" disabled={isSaving}>
                  {isSaving
                    ? 'Salvataggio...'
                    : modalMode === 'create'
                    ? 'Crea Wrestler'
                    : 'Salva Modifiche'}
                </BtnPrimary>
              </ButtonRow>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}
    </CommonWrapper>
  );
};

export default HandleWrestler;
